import { atom } from 'jotai';

/**
 * Main-window state linking the copilot's `prechart_schedule` intent to the
 * pre-chart orchestrator.
 *
 * `CopilotIntentHost` stamps each draft with the same opaque correlation when
 * the intent arrives; `useBulkSessionCreation` consumes the matching request
 * (bounded by the TTL, so an abandoned preview can't hijack a later manual
 * upload) only when the clinician opts in via `isPrechartingEnabledAtom`, then
 * publishes the created sessions to `copilotPrechartBatchAtom`, which the
 * orchestrator drains — one extraction run per patient.
 */
export interface CopilotPrechartRequest {
  correlationId: string;
  requestedAt: number;
  /**
   * EHR window handle captured from the overlay watcher before Heidi steals
   * frontmost focus on intent emit. Used to pin AX inspect during extract.
   */
  ehrWindowHandle?: number;
}

export const copilotPrechartRequestAtom = atom<CopilotPrechartRequest | null>(null);

/** Spread into request/batch payloads when the overlay pinned an EHR window. */
export const ehrWindowHandleFields = (
  ehrWindowHandle: number | null | undefined
): { ehrWindowHandle: number } | Record<string, never> =>
  ehrWindowHandle != null ? { ehrWindowHandle } : {};

/** How long a `prechart_schedule` intent stays claimable by a bulk create. */
export const COPILOT_PRECHART_REQUEST_TTL_MS = 15 * 60 * 1000;

export interface PrechartSessionTarget {
  sessionId: string;
  patientName: string;
}

/** Main-window-only identity used to resolve the debug bypass safely. */
export type CopilotActiveSessionIdentity = PrechartSessionTarget;

export const copilotActiveSessionIdentityAtom = atom<CopilotActiveSessionIdentity | null>(
  null
);

export interface PrechartBatch {
  targets: PrechartSessionTarget[];
  /**
   * Pin AX inspect to this OS window when present. Required after intent emit
   * focuses Heidi (self PID is filtered from frontmost resolution).
   */
  ehrWindowHandle?: number;
  /**
   * Debug-only: when set from a `prechart_sessions` intent, overrides the
   * main-window escalate default for this run.
   */
  allowProjectsEscalate?: boolean;
  /**
   * Sessions dropped by `toPrechartTargets` because they carried no patient
   * name. Shown in the completion toast so clinicians know what was skipped.
   */
  skipped?: number;
}

export const copilotPrechartBatchAtom = atom<PrechartBatch | null>(null);

/**
 * Debug AX Help toggle. Default on so production keeps the agent hand-off.
 * Uncheck in AX Help to stay on the local AX path for the manual test.
 */
export const copilotAllowAxEscalateAtom = atom(true);
